// Compiled by BOTH the frontend and functions-v2. Use relative imports only —
// the "@/" alias does not resolve under the functions-v2 tsconfig.
import { Vehicle } from "../types/v2/Vehicle";

/** The shape buildVehicleMakeOptions needs from a card. */
export type VehicleCard = {
  vehicle?: Vehicle | Record<string, never> | null;
};

/**
 * Canonical spellings offered in the automation builder. Not exhaustive by
 * design — buildVehicleMakeOptions unions this with the makes actually on the
 * shop's board, so anything we miss still shows up for the shops that see it.
 */
export const VEHICLE_MAKES: readonly string[] = [
  "Acura",
  "Alfa Romeo",
  "Aston Martin",
  "Audi",
  "Bentley",
  "BMW",
  "Buick",
  "Cadillac",
  "Chevrolet",
  "Chrysler",
  "Dodge",
  "Ferrari",
  "Fiat",
  "Ford",
  "Genesis",
  "GMC",
  "Honda",
  "Hummer",
  "Hyundai",
  "Infiniti",
  "Isuzu",
  "Jaguar",
  "Jeep",
  "Kia",
  "Lamborghini",
  "Land Rover",
  "Lexus",
  "Lincoln",
  "Lotus",
  "Maserati",
  "Mazda",
  "McLaren",
  "Mercedes-Benz",
  "Mercury",
  "Mini",
  "Mitsubishi",
  "Nissan",
  "Oldsmobile",
  "Plymouth",
  "Polestar",
  "Pontiac",
  "Porsche",
  "Ram",
  "Rivian",
  "Rolls-Royce",
  "Saab",
  "Saturn",
  "Scion",
  "Smart",
  "Subaru",
  "Suzuki",
  "Tesla",
  "Toyota",
  "Volkswagen",
  "Volvo",
];

/**
 * Common abbreviations/nicknames that should resolve to the same comparison
 * key as their canonical make. Keyed by the *already-normalized* alias
 * (lowercase, alphanumeric-only) — values are normalized keys too, not
 * display spellings. These reach the system via LLM-generated automations
 * (which favor short forms) and free-text vehicle.make entry in CardForm.
 *
 * Deliberately NOT included, and why:
 * - "merc"  — ambiguous: both "Mercury" and "Mercedes-Benz" are curated
 *             makes and "Merc" is used for either in the wild. Aliasing it
 *             would silently mis-route one of them.
 * - "gm"    — "GM" is not itself a make in the curated list; "GMC" is. They
 *             are not the same thing (GM is the parent conglomerate).
 * - "rover" — Rover was its own distinct marque, separate from Land Rover.
 */
const VEHICLE_MAKE_ALIASES: Readonly<Record<string, string>> = {
  alfa: "alfaromeo",
  aston: "astonmartin",
  beemer: "bmw",
  bimmer: "bmw",
  benz: "mercedesbenz",
  caddy: "cadillac",
  chev: "chevrolet",
  chevy: "chevrolet",
  infinity: "infiniti",
  mercedes: "mercedesbenz",
  mitsu: "mitsubishi",
  rolls: "rollsroyce",
  subie: "subaru",
  vdub: "volkswagen",
  volks: "volkswagen",
  vw: "volkswagen",
};

/**
 * Reduces a make to a comparison key. Integrators store the make exactly as
 * their API returned it, so "Mercedes-Benz", "Mercedes Benz" and
 * "MERCEDES BENZ" all describe the same manufacturer and must compare equal.
 * Common abbreviations (e.g. "Chevy", "VW") are also folded onto their
 * canonical make's key via VEHICLE_MAKE_ALIASES.
 * @param {string} value - The raw make string.
 * @returns {string} A lowercase, alphanumeric-only comparison key.
 */
export function normalizeVehicleMake(value: string): string {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return VEHICLE_MAKE_ALIASES[normalized] ?? normalized;
}

/**
 * Builds the automation builder's make dropdown: the curated list unioned with
 * every distinct make on the shop's live cards. The curated spelling wins when
 * a board make normalizes to the same key, so the option a user picks is
 * stable across shops.
 * @param {VehicleCard[]} cards - The shop's live cards.
 * @returns {{ value: string; label: string }[]} Options sorted by label.
 */
export function buildVehicleMakeOptions(
  cards: VehicleCard[]
): { value: string; label: string }[] {
  const byKey = new Map<string, string>();

  for (const make of VEHICLE_MAKES) {
    byKey.set(normalizeVehicleMake(make), make);
  }

  for (const card of cards) {
    const make = (card.vehicle as Vehicle | undefined)?.make;
    if (typeof make !== "string") continue;
    const key = normalizeVehicleMake(make);
    // Empty after normalization ("", "   ") is not a make. Curated spellings
    // are never overwritten by a board variant.
    if (key === "" || byKey.has(key)) continue;
    byKey.set(key, make.trim());
  }

  return [...byKey.values()]
    .map((make) => ({ value: make, label: make }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
